// ============================================================================
// WIDGET SANDBOX — PostMessage Protocol Types
// Typed discriminated unions for host ↔ sandbox communication
// ============================================================================

// --- Data API ---

export const ALLOWED_API_PATHS = [
  '/app-data/processed-days/',
  '/app-data/processed-sleeps/',
  '/app-data/processed-activities/',
  '/app-data/processing-preferences/',
  '/survey/activities/',
  '/survey/sleeps/',
  // ponytail: pending beoflow PR #1230 merge to dev (fields=agent branch) before this
  // ships to shared dev/main — safe locally now since the backend worktree has it.
  '/calendar-planning/activity-events/',
] as const

export type AllowedApiPath = (typeof ALLOWED_API_PATHS)[number]

// --- Theme Tokens (serializable subset for postMessage) ---

export interface WidgetThemeTokens {
  colors: {
    primary: string
    secondary: string
    background: string
    surface: string
    text: string
    textSecondary: string
    error: string
    warning: string
    success: string
    accent: string
  }
  fonts: {
    body: string
    mono: string
  }
  spacing: {
    xs: string
    sm: string
    md: string
    lg: string
    xl: string
  }
  borderRadius: {
    small: string
    medium: string
    large: string
    round: string
  }
}

// --- Beoflow Widget API (the bridge contract widgets use) ---

// The date range the host surface is currently showing (e.g. the calendar's
// visible week). Widgets that fetch date-scoped data should read this instead
// of computing "today" themselves, so they stay in sync when the host
// navigates to a different range. yyyy-MM-dd, inclusive.
export interface WidgetContext {
  viewStartDate: string
  viewEndDate: string
  activityPlanId: number | null
}

export interface BeoflowWidgetAPI {
  fetch: (path: string, params?: Record<string, unknown>) => Promise<unknown>
  getUser: () => Promise<{ id: string; name: string }>
  context: WidgetContext | null
}

// --- Host → Sandbox Messages ---

export interface RenderWidgetMessage {
  type: 'RENDER_WIDGET'
  code: string
  theme: WidgetThemeTokens
  context?: WidgetContext
}

// --- View mode: one iframe renders ALL of a view's widgets (shared runtime) ---

export interface ViewWidgetSpec {
  id: number
  title: string
  code: string
  isSystem?: boolean
  /** User-facing explanation shown behind the card's info icon. */
  description?: string
}

export interface ViewLayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
}

export interface RenderViewMessage {
  type: 'RENDER_VIEW'
  widgets: ViewWidgetSpec[]
  layout: ViewLayoutItem[]
  theme: WidgetThemeTokens
  readOnly?: boolean
  /** Show the per-widget actions menu (remove / refresh / edit) without enabling
   *  drag+resize. Defaults to !readOnly for callers that don't set it. */
  showActions?: boolean
  cols?: number
  rowHeight?: number
  /** Optional date window for the whole view. Exposed to widget code as the
   *  `beoflowRange` global ({days, start, end} | null); widgets that honor it
   *  re-fetch when it changes (they are remounted on a range change). */
  rangeDays?: number
  /** Which athlete's data this render is for ('' = the viewer's own). The host
   *  injects target_profile_id at REQUEST time, so the sandbox must remount its
   *  widgets when this changes or they keep the previous athlete's data
   *  (BEO-3095). Opaque to widget code — it is only a remount key. */
  profileScope?: string
}

export interface DataResponseMessage {
  type: 'DATA_RESPONSE'
  requestId: string
  data?: unknown
  error?: string
}

export interface ContainerResizeMessage {
  type: 'CONTAINER_RESIZE'
  width: number
  height: number
}

export interface DestroyMessage {
  type: 'DESTROY'
}

export type HostToSandboxMessage =
  | RenderWidgetMessage
  | RenderViewMessage
  | DataResponseMessage
  | ContainerResizeMessage
  | DestroyMessage

// --- Sandbox → Host Messages ---

export interface SandboxReadyMessage {
  type: 'SANDBOX_READY'
}

export interface DataRequestMessage {
  type: 'DATA_REQUEST'
  requestId: string
  method: 'fetch' | 'getUser'
  args: unknown[]
}

export interface ResizeMessage {
  type: 'RESIZE'
  height: number
}

export interface RenderSuccessMessage {
  type: 'RENDER_SUCCESS'
  widgetId?: number
}

export interface RenderErrorMessage {
  type: 'RENDER_ERROR'
  error: string
  stack?: string
  widgetId?: number
}

// View mode: the in-iframe grid posts these back to the host.
export interface ViewLayoutChangedMessage {
  type: 'VIEW_LAYOUT_CHANGED'
  layout: ViewLayoutItem[]
}

export interface WidgetActionMessage {
  type: 'WIDGET_ACTION'
  widgetId: number
  action: 'remove' | 'fix'
  error?: string
}

export type SandboxToHostMessage =
  | SandboxReadyMessage
  | DataRequestMessage
  | ResizeMessage
  | RenderSuccessMessage
  | RenderErrorMessage
  | ViewLayoutChangedMessage
  | WidgetActionMessage
